import './booking-summary.scss';

import SummaryHeader from './summary-header';
import SummaryModifyButton from './summary-modify-button';
import SummaryItem from './summary-item';
import SummaryCheckout from './summary-checkout';
import SummarySubtotal from './summary-subtotal';

import { CaretDownFilled } from '@ant-design/icons';
import { Affix, Col, Collapse, Row } from 'antd';
import Text from 'antd/lib/typography/Text';
import moment from 'moment';
import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import numberHelpers from '../../helpers/number';

import ZLSection from '../../containers/zl-section/zl-section';

import PackageTierPrice from '../../interfaces/IPackageTierPrice';

import { ISummarySubtotalItemProps } from './summary-subtotal-item';
import { actionCreators as BookingActions, BookingStatus } from '../../store/Booking';
import { actionCreators as GiftVoucherActions, GiftVoucherStatus } from '../../store/GiftVoucher';
import { actionCreators as PlayerActions } from '../../store/Players';
import { actionCreators as SessionActions } from '../../store/Sessions';
import { actionCreators as RewardActions } from '../../store/Rewards';
import PlayerCountUpsell from '../player-count-upsell/player-count-upsell';
import { PlayerCountUpsellFlow } from '../../helpers/PlayerCountUpsellFlow';
import useUpsellAccept from '../player-count-upsell/useUpsellAccept';
import TieredGroupSizeDiscount from '../tiered-group-size-discount/tiered-group-size-discount';
import { DISCOUNT_TIERS as TIERED_GROUP_SIZE_DISCOUNT_TIERS, CAMPAIGN_CONFIG } from '../tiered-group-size-discount/tieredGroupSizeDiscountConfig';
import useTieredGroupSizeDiscountAccept from '../tiered-group-size-discount/useTieredGroupSizeDiscountAccept';


import pluralHelper from '../../helpers/plural';
import { resolveLocalizedEntry } from '../../helpers/localization';
import { Routes, getCurrentRoute } from '../../helpers/routes';

import ZLButton, { ButtonType } from '../zl-button/zl-button';
import { IDiscountCode } from '../../interfaces/IDiscountCode';
import adBlockChecker from '../../helpers/adBlockChecker';
import { PurchaseTypes } from '../../store/PurchaseTypes';
import SummaryPrivacy from './summary-privacy';
import { IAnalyticsAction, IApplicationState } from '../../store';
import { PaymentStatusType } from '../../interfaces/IInvoice';
import { isFeatureEnabled, KnownFlag } from '../../store/featureFlags';
import { GoogleTagManagerEvents } from '../../helpers/googleTagManager';

const { Panel } = Collapse;

interface PurchaseDetailsProps {
    isFullWidth: boolean;
}

export interface IBookingSummaryProps {
    active?: boolean; //toggles display for the embedded version
    affix?: boolean; //regular desktop affix
    drawer?: boolean; //affix drawer style
    scrollTo?: boolean;
    dynamicOffsetTop?: number; //dynamic offset for coordinating with other sticky elements
}

const BookingSummary: React.FunctionComponent<IBookingSummaryProps> = (props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const location = useLocation();

    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);

    const giftVoucherState = useSelector((state: IApplicationState) => state.giftVoucher);

    const selectedLanguage = useSelector((state: IApplicationState) => state.intl.selectedLanguage);
    const site = useSelector((state: IApplicationState) => state.sites.selectedSite);
    const players = useSelector((state: IApplicationState) => state.players.selectedPlayers);
    const playersUI = useSelector((state: IApplicationState) => state.players.selectedPlayersUI);
    const selectedPrivateUpgrade = useSelector((state: IApplicationState) => state.players.selectedPrivateUpgrade);

    const date = useSelector((state: IApplicationState) => state.session.selectedDate);
    const time = useSelector((state: IApplicationState) => state.session.selectedTime);
    const session = useSelector((state: IApplicationState) => state.session.selectedSession);
    const sessionUI = useSelector((state: IApplicationState) => state.session.selectedSessionUI);

    const bookingDiscountCodes = useSelector((state: IApplicationState) => state.booking.appliedCodes);
    const invoiceDiscountCodes = useSelector((state: IApplicationState) => state.invoice.appliedCodes);
    const pendingBooking = useSelector((state: IApplicationState) => state.booking.booking);
    const bookingStatus = useSelector((state: IApplicationState) => state.booking.status);
    const invoiceStatus = useSelector((state: IApplicationState) => state.invoice.paymentStatusTypeId);
    const feeAmount = useSelector((state: IApplicationState) => state.sites.bookingOptions?.feeAmount);
    const feePercent = useSelector((state: IApplicationState) => state.sites.bookingOptions?.feePercent);
    const reservationBooking = useSelector((state: IApplicationState) => state.sites.selectedSite?.reservationPortal);

    const bookingLoading = useSelector((state: IApplicationState) => state.booking.loading);
    const giftVoucherLoading = useSelector((state: IApplicationState) => state.giftVoucher.loading);
    const invoiceLoading = useSelector((state: IApplicationState) => state.invoice.loading);

    const bookingCharge = useSelector((state: IApplicationState) => state.booking.charge);
    const giftVoucherCharge = useSelector((state: IApplicationState) => state.giftVoucher.charge);
    const invoiceCharge = useSelector((state: IApplicationState) => state.invoice.charge);

    const voucherCurrency = useSelector((state: IApplicationState) => state.giftVoucher.selectedVoucherCurrency);
    const voucherAmount = useSelector((state: IApplicationState) => state.giftVoucher.amount);
    const pendingVoucher = useSelector((state: IApplicationState) => state.giftVoucher.giftVoucher);
    const toRecipient = useSelector((state: IApplicationState) => state.giftVoucher.toRecipientSelected);
    const recipientName = useSelector((state: IApplicationState) => state.giftVoucher.toFullName);
    const recipientEmail = useSelector((state: IApplicationState) => state.giftVoucher.toEmail);
    const recipientDetailsValid = useSelector((state: IApplicationState) => state.giftVoucher.recipientDetailsValid);
    const recipientMessage = useSelector((state: IApplicationState) => state.giftVoucher.message);
    const giftVoucherStatus = useSelector((state: IApplicationState) => state.giftVoucher.status);
    const giftVoucherDiscountPercentage = useSelector((state: IApplicationState) => state.giftVoucher.configDetails?.activeDiscount?.discountPercentage);

    const attractionTypes = useSelector((state: IApplicationState) => state.sites.bookingOptions?.attractionTypes);
    const maximumPlayers = useSelector((state: IApplicationState) => state.sites.bookingOptions?.maximumPlayers ?? 0);
    const selectedPaymentOption = useSelector((state: IApplicationState) => state.paymentOption.selectedOption);

    const rewardFlows = useSelector((state: IApplicationState) => state.rewards.flows);
    const upsellWasShown = useSelector((state: IApplicationState) => state.rewards.upsellWasShown);
    const featureFlags = useSelector((state: IApplicationState) => state.featureFlags);
    const upsellFlagEnabled = isFeatureEnabled(featureFlags, KnownFlag.playerCountUpsell);
    const tieredGroupSizeDiscountEnabled = CAMPAIGN_CONFIG.enabled;
    const selectedAddOns = useSelector((state: IApplicationState) => state.booking.selectedAddOns);
    const selectedDigitalAddOns = useSelector((state: IApplicationState) => state.booking.selectedDigitalAddOns);

    const ptvPurchaseName = useSelector((state: IApplicationState) => state.premiumPtv.purchaseName);
    const ptvCreditPlayerCount = useSelector((state: IApplicationState) => state.premiumPtv.creditPlayerCount);
    const ptvPurchasePlayerCount = useSelector((state: IApplicationState) => state.premiumPtv.purchasePlayerCount);
    const ptvPrice = useSelector((state: IApplicationState) => state.premiumPtv.price);

    const [panelOpen, setPanelOpen] = useState(false);
    const activeKey = panelOpen ? 'summary' : '';

    const page = getCurrentRoute(location.pathname);
    const ticketsPage = page === Routes.BookNow || page === Routes.PrivateEvent || page === undefined;
    const addOnsPage = page === Routes.Addons;
    const giftVouchersPage = page === Routes.GiftVoucher || page === Routes.RegionalGiftVoucher;
    const checkoutPage = page === Routes.Checkout;

    const gamePackage = session?.package;
    const priceTierTypeId = session?.priceTierTypeId;
    const packageTierPrice = session?.package.packageTierPrices.find(
        (ptp: PackageTierPrice) => ptp.priceTierType.priceTierTypeId === priceTierTypeId
    );

    const attractionType = useMemo(() => {
        return attractionTypes?.find((a) => a.attractionTypeId === session?.gameSpace.attractionTypeId);
    }, [attractionTypes, session?.gameSpace.attractionTypeId]);

    const discountCodes = useMemo(() => selectedPurchaseType === PurchaseTypes.PremiumPtv ? invoiceDiscountCodes : bookingDiscountCodes, [selectedPurchaseType, invoiceDiscountCodes, bookingDiscountCodes]);

    const isPackagePurchase = selectedPurchaseType === PurchaseTypes.Booking || selectedPurchaseType === PurchaseTypes.PrivateEvent;
    const isGiftVoucherPurchase = selectedPurchaseType === PurchaseTypes.GiftVoucher || selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher
    const isGlobalPurchase = selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher;

    const loading = invoiceLoading || bookingLoading || giftVoucherLoading;
    const charge = invoiceCharge ? invoiceCharge : (isPackagePurchase ? bookingCharge : giftVoucherCharge);

    const completedVoucherAmount = (voucherAmount ?? 0) > 0;

    const bookingCheckoutEnabled =
        isPackagePurchase && (packageTierPrice?.price !== undefined || charge !== undefined) && (ticketsPage || addOnsPage);

    const giftVoucherCheckoutEnabled =
        giftVouchersPage && isGiftVoucherPurchase &&
        completedVoucherAmount &&
        (recipientDetailsValid || !(toRecipient ?? true));

    const checkoutEnabled = bookingCheckoutEnabled || giftVoucherCheckoutEnabled;

    const pricing = packageTierPrice?.price;
    const currencyLocale = charge?.currencyLocale ?? pricing?.currencyLocale;
    const currencyCode = charge?.currencyCode ?? pricing?.currencyCode ?? '';
    const taxInclusive = charge?.taxInclusive ?? pricing?.taxInclusive ?? false;

    let tax = charge?.tax ?? pricing?.tax ?? 0;
    let fee = charge?.fee ?? 0;
    let ticketCost = charge?.cost ?? pricing?.cost ?? 0;
    let total = charge?.total ?? pricing?.total ?? 0;

    // Calculations to display Value / Discount before charge is created (Before checkout is pressed).
    let selectedGiftVoucherTotalAfterDiscount = (voucherAmount ?? 0) * (1 - (giftVoucherDiscountPercentage ?? 0));
    let selectedGiftVoucherDiscountAmount = (voucherAmount ?? 0) * (giftVoucherDiscountPercentage ?? 0) * -1;

    let giftVoucherTax = charge?.tax ?? site?.taxPercentage ?? 0;
    let giftVoucherFee = charge?.fee ?? 0;
    let giftVoucherTotal = charge?.total ?? selectedGiftVoucherTotalAfterDiscount ?? 0;

    // Used to display the giftVoucherTotal after a purchase has been made
    // (charge.total = 0 because it has been paid for)
    if (giftVoucherTotal === 0 && giftVoucherStatus === GiftVoucherStatus.Completed) {
        giftVoucherTotal = selectedGiftVoucherTotalAfterDiscount ?? 0;
    }

    let adBlockDetected = false;
    adBlockChecker.IsAdBlockEnabled().then((flag) => {
        adBlockDetected = flag;
    });

    const missingPlayers =
        session && session.maximumSlots === session.remainingSlots ? session.maximumSlots - (players ?? 8) : 0;
    let privateSessionCost = session
        ? (missingPlayers * (pricing?.cost ?? 0) * session.package.privacyChargePercentage) / 100
        : 0;
    const privateSessionTax = session
        ? (missingPlayers * (pricing?.tax ?? 0) * session.package.privacyChargePercentage) / 100
        : 0;

    const inferedCurrency =
        isGiftVoucherPurchase
            ? (voucherCurrency?.code ?? site?.paypalCode ?? '')
            : (currencyCode ?? site?.paypalCode ?? '')

    //a package has been selected but there is no charge object from the server.
    //pricing from packages are based on 1 ticket, so the tax and total needs to be for all tickets
    if (players !== undefined && (addOnsPage || (charge === undefined && pricing !== undefined))) {
        tax = pricing?.tax ?? 0;
        fee = 0;
        ticketCost = pricing?.cost ?? 0;
        total = pricing?.total ?? 0;

        if (gamePackage?.perPersonPricing) {
            tax *= players;
            total *= players;
            ticketCost *= players;

            if (taxInclusive) {
                ticketCost += tax;
            }
        }

        if (gamePackage?.privacyTypeId === 2 && selectedPrivateUpgrade) {
            tax += privateSessionTax;
            total += privateSessionCost + privateSessionTax;
        }

        selectedAddOns?.forEach((selectedAddOn) => {
            if(selectedAddOn.quantity) {
                tax += selectedAddOn.price.tax * selectedAddOn.quantity;
                total += selectedAddOn.price.total * selectedAddOn.quantity;
            }
        });

        selectedDigitalAddOns?.forEach((selectedAddOn) => {
            if(selectedAddOn.quantity) {
                tax += selectedAddOn.price.tax * selectedAddOn.quantity;
                total += selectedAddOn.price.total * selectedAddOn.quantity;
            }
        });
        
        if (feeAmount !== undefined && feePercent !== undefined) {
            fee = feeAmount + (total * (feePercent / 100));
            total += fee;
        }
    }

    if (taxInclusive) {
        privateSessionCost += privateSessionTax;
    }

    const title = props.affix
        ? t('bookingSummary.titles.summary') //affixed title
        : t('bookingSummary.titles.purchaseSummary'); // all other titles


    const globalTitle = t('bookingSummary.global.title');
    const globalLabeltranslation = t('bookingSummary.global.label', { currency: voucherCurrency?.code });
    const globalLabel = <Text>{globalLabeltranslation}</Text>
    const venueTitle = t('bookingSummary.labels.venue');
    const playersTitle = t('bookingSummary.labels.players');
    const dateTitle = t('bookingSummary.labels.date');
    const timeTitle = t('bookingSummary.labels.sessionTime');
    const modifyText = t('bookingSummary.labels.modify');
    const packageTitle = t('bookingSummary.labels.experiences');

    const amountTitle = t('giftVoucherSection.summary.amountLabel');
    const recipientDetailsTitle = t('giftVoucherSection.summary.detailsLabel');
    const recipientMessageTitle = t('giftVoucherSection.summary.messageLabel');
    const expirationMessageTitle = t('giftVoucherSection.details.expirationLabel');

    const arriveEarlyTime = attractionType?.arrivalTimeMinutes ?? 0;

    const arriveEarlyText =
        arriveEarlyTime && arriveEarlyTime > 0
            ? reservationBooking
                ? t('arriveEarlyReservation', { value: arriveEarlyTime })
                : t('arriveEarly', { value: arriveEarlyTime })
            : undefined;

    const playersText =
        players !== undefined
            ? t(pluralHelper.getKey(selectedLanguage.code, 'bookingSummary.labels.numberOfPlayers', players), { count: players })
            : '';

    const dateText = date !== undefined ? moment(date).format('D MMMM YYYY') : '';

    const startTime = moment(time?.time).utc().format('LT');
    const endTime =
        gamePackage !== undefined
            ? `${moment(time?.time)
                  .add(gamePackage?.experienceDurationMinutes ?? 0, 'minutes')
                  .utc()
                  .format('LT')}`
            : undefined;

    const timeText = time && endTime ? `${startTime} - ${endTime}` : endTime === undefined ? `${startTime}` : '';

    const packageName =
        gamePackage !== undefined
            ? (resolveLocalizedEntry(gamePackage.languageData, selectedLanguage.code, site?.defaultLocale)?.name ?? gamePackage.alias)
            : '';

    const voucherAmountText = voucherAmount !== undefined ? voucherAmount.toFixed(2).toString() : '';

    const modifyClicked = useCallback(() => {
        if (isPackagePurchase) {
            if (pendingBooking) {
                dispatch(BookingActions.modifyBooking(pendingBooking));
            }
        } else if (isGiftVoucherPurchase) {
            if (pendingVoucher) {
                dispatch(GiftVoucherActions.modifyGiftVoucher(pendingVoucher));
            }
        }
    }, [pendingBooking, pendingVoucher, dispatch, isGiftVoucherPurchase, isPackagePurchase]);

    const privacySelected = useCallback(
        (isPrivate: boolean) => {
            if (pendingBooking && bookingStatus === BookingStatus.Pending && selectedPrivateUpgrade !== isPrivate) {
                dispatch(BookingActions.expireBooking());
            }

            dispatch(PlayerActions.selectPrivateUpgrade(isPrivate));
        },
        [dispatch, pendingBooking, bookingStatus, selectedPrivateUpgrade]
    );

    const checkoutClicked = useCallback(() => {
        //when moving to the checkout page, make sure to abandon any old data that is no longer relevant
        if (addOnsPage) {
            dispatch(BookingActions.applyAddOns());
        } else if (isPackagePurchase) {
            dispatch(GiftVoucherActions.cancelGiftVoucher());
            dispatch(GiftVoucherActions.clearGiftVoucher());

            if (pendingBooking && bookingStatus === BookingStatus.Pending) {
                dispatch(BookingActions.continuePendingBooking());
            } else if (session) {
                dispatch(BookingActions.reserveBooking(session, adBlockDetected));
            }
        } else if (isGiftVoucherPurchase) {
            dispatch(BookingActions.expireBooking());
            dispatch(BookingActions.clearBooking());
            dispatch(PlayerActions.clearPlayers());
            dispatch(SessionActions.clearSelectedDateTime());
            dispatch(SessionActions.clearSelectedTime());

            //if we have a pending gift voucher push to the checkout page
            if (giftVoucherStatus === GiftVoucherStatus.Pending) {
                dispatch(
                    GiftVoucherActions.updateGiftVoucherAmount(() =>
                        dispatch(GiftVoucherActions.continuePendingGiftVoucher())
                    )
                );
            } else if (giftVoucherState) {
                //otherwise attempt to create the voucher and set it to pending
                if (selectedPurchaseType === PurchaseTypes.GlobalGiftVoucher) {
                    dispatch(GiftVoucherActions.reserveRegionalGiftVoucher(adBlockDetected));
                }
                else if (site !== undefined) {
                    dispatch(GiftVoucherActions.reserveGiftVoucher(adBlockDetected));
                }
                else {
                    console.log(`Unable to checkout gift voucher, voucher is not global and or no site is selected`);
                }
            }
        }
    }, [
        giftVoucherState,
        site,
        pendingBooking,
        bookingStatus,
        giftVoucherStatus,
        session,
        dispatch,
        selectedPurchaseType,
        addOnsPage,
        isGiftVoucherPurchase,
        isPackagePurchase,
        adBlockDetected
    ]);

    const toggleDrawer = useCallback(() => {
        setPanelOpen(!panelOpen);
    }, [panelOpen]);

    // --- PlayerCountUpsell (mobile drawer) ---
    const upsellFlow = useMemo(() => new PlayerCountUpsellFlow(rewardFlows), [rewardFlows]);
    const upsellEligible = useMemo(() => {
        return upsellFlow.isEligible(
            upsellFlagEnabled,
            players,
            selectedPurchaseType,
            session?.package?.attractionTypeId,
            selectedPrivateUpgrade,
            session?.remainingSlots,
            maximumPlayers
        );
    }, [upsellFlow, upsellFlagEnabled, players, selectedPurchaseType, session, selectedPrivateUpgrade, maximumPlayers]);

    const isOfferMet = players != null && upsellFlow.isOfferMet(players);
    const showMobileUpsell = props.drawer && session && (upsellEligible || (upsellWasShown && !selectedPrivateUpgrade && upsellFlow.isPackageEligible(session.package?.attractionTypeId)));

    const checkoutText = (upsellWasShown && !isOfferMet && ticketsPage)
        ? <>
                <span className="checkout-label">{t('bookingSummary.labels.checkout')}</span>
                <span className="checkout-subtitle">{t('bookingSummary.labels.checkoutWithoutOfferSubtitle')}</span>
            </>
        : t('bookingSummary.labels.checkout');

    useEffect(() => {
        if (showMobileUpsell && !upsellWasShown && players) {
            dispatch(RewardActions.upsellInitialCount(players));
            dispatch(RewardActions.upsellWasShown('drawer', players, upsellFlow.minimumTicketCount));
        }
    }, [showMobileUpsell, upsellWasShown, players, upsellFlow.minimumTicketCount, dispatch]);

    useEffect(() => {
        if (upsellWasShown && (!upsellFlagEnabled || selectedPrivateUpgrade)) {
            dispatch(RewardActions.upsellReset());
        }
    }, [upsellFlagEnabled, upsellWasShown, selectedPrivateUpgrade, dispatch]);

    const handleUpsellAccept = useUpsellAccept(upsellFlow);

    // --- TieredGroupSizeDiscount (mobile drawer) ---
    const isBookingReserved = pendingBooking && bookingStatus === BookingStatus.Pending;
    const showMobileTieredGroupSizeDiscount = props.drawer
        && tieredGroupSizeDiscountEnabled
        && selectedPurchaseType === PurchaseTypes.Booking
        && session != null
        && (CAMPAIGN_CONFIG.attractionTypeId === undefined || session.package?.attractionTypeId === CAMPAIGN_CONFIG.attractionTypeId)
        && players != null
        && !isBookingReserved
        && Math.min(maximumPlayers, session.remainingSlots ?? 0) >= TIERED_GROUP_SIZE_DISCOUNT_TIERS[0].threshold;

    const tieredGroupSizeDiscountShownRef = React.useRef(false);
    useEffect(() => {
        if (showMobileTieredGroupSizeDiscount && players && !tieredGroupSizeDiscountShownRef.current) {
            tieredGroupSizeDiscountShownRef.current = true;
            dispatch({
                type: 'TIERED_GROUP_SIZE_DISCOUNT_SHOWN',
                googleTagManagerEvent: GoogleTagManagerEvents.TieredGroupSizeDiscountShown('drawer', players),
            } as IAnalyticsAction);
        }
    }, [showMobileTieredGroupSizeDiscount, players, dispatch]);

    const handleTieredGroupSizeDiscountAccept = useTieredGroupSizeDiscountAccept();

    // Formatting for Bookings
    const venueNode = site ? (
        <React.Fragment>
            <Text>{`${site.siteMetadata.addressLine1}`}</Text>
            <br />
            <Text>{`${site.siteMetadata.addressLine2}`}</Text>
            <br />
            <Text>{`${site.siteMetadata.addressLine3}`}</Text>
        </React.Fragment>
    ) : (
        <React.Fragment />
    );

    const modifyButton = checkoutPage || addOnsPage ? (
        <SummaryModifyButton text={modifyText} onClick={modifyClicked} />
    ) : (
        <React.Fragment />
    );

    const formattedPlayers = players ? <Text>{playersText}</Text> : undefined;
    const formattedDate = date ? <Text>{dateText}</Text> : undefined;
    const formattedDuration = numberHelpers.toNumber(selectedLanguage.code, gamePackage?.experienceDurationMinutes ?? 0);
    const formattedTime = time ? (
        <React.Fragment>
            <Text>{timeText}</Text>
            <br />
            <Text>{arriveEarlyText}</Text>
        </React.Fragment>
    ) : undefined;

    const formattedPackage = gamePackage ? (
        <Text>{t('bookingSummary.labels.gamePackageValue', { name: packageName, duration: formattedDuration })}</Text>
    ) : undefined;

    // Formatting for Gift Vouchers (if the voucher has a currency eg. from being global, use that as a priority)
    const formattedVoucherAmount = voucherAmount ? (
        <Text>{(inferedCurrency) + ' ' + voucherAmountText}</Text>
    ) : undefined;

    const formattedRecipientDetails =
        recipientName && recipientEmail ? (
            <React.Fragment>
                <Text>{recipientName}</Text>
                <br />
                <Text>{recipientEmail}</Text>
            </React.Fragment>
        ) : undefined;
    const formattedMessage = recipientMessage ? <Text>{recipientMessage}</Text> : undefined;

    const expiryYears = useSelector((state: IApplicationState) => state.giftVoucher.configDetails?.expiryYears);

    let expirationDate = new Date();
    let expirationDateString = expirationDate.toDateString();

    if (expiryYears) {
        expirationDate.setFullYear(expirationDate.getFullYear() + expiryYears);
        expirationDateString = expirationDate.toDateString();
        let parts = expirationDateString.split(' ');
        expirationDateString = parts.slice(1).join(' ');
    }

    const formattedExpirationDate = expiryYears ? <Text>{expirationDateString}</Text> : '';

    const isPending = bookingStatus === BookingStatus.Pending || invoiceStatus === PaymentStatusType.AwaitingPayment;

    const isSplitSelected =
        selectedPaymentOption === 'split' &&
        isPackagePurchase &&
        charge?.isSplitPayment === true &&
        (charge?.splitPaymentTotal ?? 0) > 0;

    const splitPayingSlots = charge?.splitPaymentSlotCount ?? 0;
    const splitTotalSlots = charge?.splitPaymentTotalSlotCount ?? 0;
    const splitRemainingSlots = Math.max(splitTotalSlots - splitPayingSlots, 0);

    //Subtotals
    let subtotalItems: Array<ISummarySubtotalItemProps> = [];

    let giftVoucherSubtotalItems: Array<ISummarySubtotalItemProps> = [];

    if (gamePackage?.perPersonPricing) {
        //players item
        if (players !== undefined) {
            const splitTicketValue =
                (charge?.splitPaymentPerTicketPrice ?? 0) > 0
                    ? (charge!.splitPaymentPerTicketPrice ?? 0) * splitPayingSlots
                    : charge?.splitPaymentAmount ?? 0;

            const ticketLabel = isSplitSelected
                ? t(pluralHelper.getKey(selectedLanguage.code, 'bookingSummary.labels.ticketSplit', splitPayingSlots), {
                      paying: splitPayingSlots,
                      total: splitTotalSlots,
                      count: splitPayingSlots,
                  })
                : t(pluralHelper.getKey(selectedLanguage.code, 'bookingSummary.labels.ticket', players), { count: players });

            // Remove Tax for display purposes
            subtotalItems = [
                ...subtotalItems,
                {
                    label: ticketLabel,
                    value: isSplitSelected ? splitTicketValue : ticketCost,
                    currency: inferedCurrency,
                    locale: selectedLanguage.code ?? currencyLocale,
                },
            ];
        }
    } else {
        if (session) {
            // Remove Tax for display purposes
            subtotalItems = [
                ...subtotalItems,
                {
                    label: t('statuses.players_range_requirement', {
                        minimum: session?.minimumSlots,
                        maximum: session?.maximumSlots,
                    }),
                    value: ticketCost,
                    currency: inferedCurrency,
                    locale: selectedLanguage.code ?? currencyLocale,
                },
            ];
        }
    }

    // Include Private Session Cost
    if (gamePackage?.privacyTypeId === 2 && selectedPrivateUpgrade && session) {
        subtotalItems = [
            ...subtotalItems,
            {
                label: t('bookingSummary.labels.privateSession'),
                value: privateSessionCost,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
            },
        ];
    }

    const addOnLabelSuffix = isSplitSelected ? ` (${t('bookingSummary.labels.youPay')})` : '';

    //Digital Add Ons
    if (selectedDigitalAddOns !== undefined && selectedDigitalAddOns.length > 0) {
        selectedDigitalAddOns.forEach((digitalAddOn) => {
            if (digitalAddOn.quantity > 0) {
                // Remove Tax for display purposes
                subtotalItems = [
                    ...subtotalItems,
                    {
                        label: digitalAddOn.quantity + " x " + digitalAddOn.name + addOnLabelSuffix,
                        value: digitalAddOn.price.listPrice * digitalAddOn.quantity,
                        currency: inferedCurrency,
                        locale: selectedLanguage.code ?? currencyLocale,
                        removeDigitalAddOn: isPending ? digitalAddOn.digitalAddOnOptionId : undefined
                    },
                ];
            }
        });
    }

    //Add Ons
    if (selectedAddOns !== undefined && selectedAddOns.length > 0) {
        selectedAddOns.forEach((addOn) => {
            if (addOn.quantity > 0) {
                // Remove Tax for display purposes
                subtotalItems = [
                    ...subtotalItems,
                    {
                        label: addOn.quantity + " x " + addOn.addOnName + " (" + addOn.optionName + ")" + addOnLabelSuffix,
                        value: addOn.price.listPrice * addOn.quantity,
                        currency: inferedCurrency,
                        locale: selectedLanguage.code ?? currencyLocale,
                        removeAddOn: isPending ? addOn.addOnOptionId : undefined
                    },
                ];
            }
        });
    }

    //gift voucher item
    if (voucherAmount !== undefined) {
        // Remove Tax for display purposes
        giftVoucherSubtotalItems = [
            ...giftVoucherSubtotalItems,
            {
                label: t('giftVoucherSection.summary.amountLabel'),
                value: voucherAmount,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
            },
        ];
    }

    //Post Game Credits
    if (ptvPurchaseName !== undefined && ptvCreditPlayerCount && ptvCreditPlayerCount > 0) {
        if(ptvPrice) {
            subtotalItems = [
                ...subtotalItems,
                {
                    label: ptvCreditPlayerCount + " x " + ptvPurchaseName + " " + t('bookingSummary.labels.credits'),
                    value: 0,
                    currency: inferedCurrency,
                    locale: selectedLanguage.code ?? currencyLocale,
                },
            ];
        }
    }

    //Post Game Purchases
    if (ptvPurchaseName !== undefined && ptvPurchasePlayerCount && ptvPurchasePlayerCount > 0) {
        if(ptvPrice) {
            subtotalItems = [
                ...subtotalItems,
                {
                    label: ptvPurchasePlayerCount + " x " + ptvPurchaseName,
                    value: ptvPrice?.listPrice * ptvPurchasePlayerCount,
                    currency: inferedCurrency,
                    locale: selectedLanguage.code ?? currencyLocale,
                },
            ];
        }
    }

    //tax item
    if (taxInclusive && tax > 0 && site?.showTaxAmountWhenBooking) {
        subtotalItems = [
            ...subtotalItems,
            {
                label: t('bookingSummary.labels.tax'),
                value: tax,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
            },
        ];

        giftVoucherSubtotalItems = [
            ...giftVoucherSubtotalItems,
            {
                label: t('bookingSummary.labels.tax'),
                value: giftVoucherTax,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
            },
        ];
    }

    //promo codes and discounts
    discountCodes.forEach((item: IDiscountCode) => {
        if (item.isPromoCode === true) {
            //console.log(item);
            subtotalItems.push({
                label: item.code,
                value: item.amount * -1,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
                removeCode: isPending ? item.code : undefined,
            });
        }
    });

    //tax item
    if (!taxInclusive && tax > 0) {
        subtotalItems = [
            ...subtotalItems,
            {
                label: t('bookingSummary.labels.tax'),
                value: tax,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
            },
        ];

        giftVoucherSubtotalItems = [
            ...giftVoucherSubtotalItems,
            {
                label: t('bookingSummary.labels.tax'),
                value: giftVoucherTax,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
            },
        ];
    }

    //promo codes and discounts
    discountCodes.forEach((item: IDiscountCode) => {
        if (!item.isPromoCode) {
            subtotalItems.push({
                label: t('bookingSummary.labels.giftVoucher'),
                value: item.amount * -1,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
                removeCode: isPending ? item.code : undefined,
            });
        }
    });

    // Displaying the discount for the GlobalGiftVouchers
    if (isGiftVoucherPurchase && selectedGiftVoucherDiscountAmount !== 0) {
        giftVoucherSubtotalItems.push({
            label: t('bookingSummary.labels.discount'), 
            value: selectedGiftVoucherDiscountAmount,
            currency: inferedCurrency,
            locale: selectedLanguage.code ?? currencyLocale
        })
    }

    const isDepositSelected = selectedPaymentOption === 'deposit' && isPackagePurchase && charge && (charge.depositTotal ?? 0) > 0;

    if (isDepositSelected) {
        subtotalItems = [
            ...subtotalItems,
            {
                label: t('bookingSummary.labels.depositAmount'),
                value: charge!.depositAmount ?? 0,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
            },
        ];
    }

    const effectiveFee = isDepositSelected
        ? charge?.depositFee ?? 0
        : isSplitSelected
        ? charge?.splitPaymentFee ?? 0
        : fee;

    if (effectiveFee > 0) {
        subtotalItems = [
            ...subtotalItems,
            {
                label: t('bookingSummary.labels.fee'),
                value: effectiveFee,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
            },
        ];

        giftVoucherSubtotalItems = [
            ...giftVoucherSubtotalItems,
            {
                label: t('bookingSummary.labels.fee'),
                value: giftVoucherFee,
                currency: inferedCurrency,
                locale: selectedLanguage.code ?? currencyLocale,
            },
        ];
    }

    const totalItem: ISummarySubtotalItemProps = {
        label: isDepositSelected
            ? t('bookingSummary.labels.depositTotal')
            : isSplitSelected
            ? t('bookingSummary.labels.yourShareTotal')
            : t('bookingSummary.labels.total'),
        value: isDepositSelected
            ? charge!.depositTotal!
            : isSplitSelected
            ? charge!.splitPaymentTotal!
            : total,
        className: 'payable-total',
        currency: inferedCurrency,
        locale: selectedLanguage.code ?? currencyLocale,
    };

    const taxItem: ISummarySubtotalItemProps | undefined =
        taxInclusive && tax > 0 && !site?.showTaxAmountWhenBooking
            ? {
                  label: 'bookingSummary.labels.includesTaxes',
                  long: true, //long items auto translate
                  value: tax,
                  className: 'includesTaxes',
                  currency: inferedCurrency,
                  locale: selectedLanguage.code ?? currencyLocale,
              }
            : undefined;

    const giftVoucherTotalItem: ISummarySubtotalItemProps = {
        label: t('bookingSummary.labels.total'),
        value: giftVoucherTotal,
        className: 'payable-total',
        currency: inferedCurrency,
        locale: selectedLanguage.code ?? currencyLocale,
    };

    const giftVoucherTaxItem: ISummarySubtotalItemProps | undefined =
        taxInclusive && giftVoucherTax > 0 && !site?.showTaxAmountWhenBooking
            ? {
                  label: 'bookingSummary.labels.includesTaxes',
                  long: true, //long items auto translate
                  value: giftVoucherTax,
                  className: 'includesTaxes',
                  currency: inferedCurrency,
                  locale: selectedLanguage.code ?? currencyLocale,
              }
            : undefined;


    const PurchaseDetails: React.FunctionComponent<PurchaseDetailsProps> = ({ isFullWidth = true }) => {
        if (isPackagePurchase) {
            return (
                <div>
                    <SummaryItem title={playersTitle} value={formattedPlayers} fullWidth={isFullWidth} />
                    <SummaryItem title={dateTitle} value={formattedDate} fullWidth={isFullWidth} />
                    <SummaryItem title={timeTitle} value={formattedTime} fullWidth={isFullWidth} />
                    <SummaryItem title={packageTitle} value={formattedPackage} fullWidth={isFullWidth} />
                    <SummaryPrivacy
                        privacyTypeId={!ticketsPage || privateSessionCost === 0 ? 0 : session?.package.privacyTypeId}
                        upgradeChecked={selectedPrivateUpgrade}
                        onCheckChanged={privacySelected}
                    />

                    <SummarySubtotal
                        currency={inferedCurrency}
                        items={subtotalItems}
                        total={totalItem}
                        inclusiveTaxItem={taxItem}
                    />
                    {isSplitSelected && splitRemainingSlots > 0 && (
                        <div className="split-remaining-note">
                            <Text type="secondary">
                                {t(
                                    pluralHelper.getKey(
                                        selectedLanguage.code,
                                        'bookingSummary.labels.splitRemainingNote',
                                        splitRemainingSlots
                                    ),
                                    { count: splitRemainingSlots }
                                )}
                            </Text>
                        </div>
                    )}
                </div>
            );
        } else if (isGiftVoucherPurchase) {
            if (isGlobalPurchase) {
                return (
                    <div>
                        <SummaryItem title={amountTitle} value={formattedVoucherAmount} fullWidth={isFullWidth} />
                        <RecipientDetails />
                        <SummaryItem
                            title={expirationMessageTitle}
                            value={formattedExpirationDate}
                            fullWidth={isFullWidth}
                        />
                        <SummarySubtotal
                            // This is displaying incorrectly on the /complete/ screen
                            currency={inferedCurrency}
                            items={giftVoucherSubtotalItems}
                            total={giftVoucherTotalItem}
                            inclusiveTaxItem={giftVoucherTaxItem}
                            hide={voucherAmount == null}
                        />
                    </div>
                );
            }
            return (
                <div>
                    <SummaryItem title={amountTitle} value={formattedVoucherAmount} fullWidth={isFullWidth} />
                    <RecipientDetails />
                    <SummaryItem
                        title={expirationMessageTitle}
                        value={formattedExpirationDate}
                        fullWidth={isFullWidth}
                    />
                    <SummarySubtotal
                        currency={inferedCurrency}
                        items={giftVoucherSubtotalItems}
                        total={giftVoucherTotalItem}
                        inclusiveTaxItem={giftVoucherTaxItem}
                        hide={voucherAmount == null}
                    />
                </div>
            );
        } else if (selectedPurchaseType === PurchaseTypes.PremiumPtv) {
            return (
                <div>
                    <SummarySubtotal
                        currency={inferedCurrency}
                        items={subtotalItems}
                        total={totalItem}
                        inclusiveTaxItem={taxItem}
                    />
                </div>
            );
        } else {
            return <React.Fragment></React.Fragment>;
        }
    };

    const RecipientDetails: React.FunctionComponent = () => {
        if (toRecipient) {
            return (
                <div>
                    <SummaryItem title={recipientDetailsTitle} value={formattedRecipientDetails} fullWidth={true} />
                    <SummaryItem title={recipientMessageTitle} value={formattedMessage} fullWidth={true} />
                </div>
            );
        } else {
            return <React.Fragment></React.Fragment>;
        }
    };

    const hideBooking = site === undefined && players === undefined;
    //TODO only hide if site not selected for regular gift vouchers
    //const hideGiftVoucher = (site === undefined) && charge === undefined;
    const hideGiftVoucher = false;

    const isHide = isPackagePurchase ? hideBooking : hideGiftVoucher;

    //desktop affix mode
    if (props.affix === true) {
        const show = isHide ? '' : 'show';
        // Calculate dynamic offset: use provided dynamicOffsetTop, fallback to original logic
        const baseOffset = charge ? 64 : 16;
        const dynamicOffset = (props.dynamicOffsetTop ?? 0) + baseOffset;

        return (
            <Affix offsetTop={dynamicOffset}>
                <div id="summaryContainer" className={`summary-container affixed ${show}`}>
                    <Row justify="start" align="top">
                        <Col span={24}>
                            <div className="summary-content">
                                <SummaryHeader
                                    modify={checkoutPage || addOnsPage}
                                    modifyText={modifyText}
                                    onModify={modifyClicked}
                                    text={title}
                                />
                                <div className="summary-items">
                                    {isGlobalPurchase ?
                                        <SummaryItem title={globalTitle} value={globalLabel} fullWidth={true} />
                                        : <SummaryItem title={venueTitle} value={venueNode} fullWidth={true} />}

                                    <PurchaseDetails isFullWidth={true} />
                                </div>

                                <SummaryCheckout
                                    enable={checkoutEnabled}
                                    loading={loading}
                                    text={checkoutText}
                                    onCheckout={checkoutClicked}
                                />
                            </div>
                        </Col>
                    </Row>
                </div>
            </Affix>
        );
    }

    //mobile drawer style
    if (props.drawer === true) {
        const show = isPackagePurchase
            ? playersUI && (sessionUI || charge)
            : completedVoucherAmount && (recipientDetailsValid || !(toRecipient ?? true));
        const drawerClass = show ? 'show' : '';
        const pointerEvents = !show ? 'none' : 'all';
        return (
            <Affix offsetBottom={0} style={{ pointerEvents: pointerEvents }}>
                <div id="summaryContainer" className={`summary-container drawer ${drawerClass}`}>
                    <Row justify="start" align="top">
                        <Col xs={24} sm={24} md={24} lg={0}>
                            {showMobileUpsell && players && (
                                <PlayerCountUpsell
                                    currentPlayers={players}
                                    minimumPlayers={upsellFlow.minimumTicketCount}
                                    maximumPlayers={Math.min(maximumPlayers, session?.remainingSlots ?? 0)}
                                    locale={selectedLanguage.code}
                                    onAccept={handleUpsellAccept}
                                    variant="drawer"
                                    accepted={isOfferMet}
                                    ticketPrice={pricing?.listPrice}
                                    currencyCode={currencyCode}
                                    loading={bookingLoading}
                                    onExpand={() => dispatch(RewardActions.upsellCardExpanded())}
                                />
                            )}
                            {showMobileTieredGroupSizeDiscount && players && (
                                <TieredGroupSizeDiscount
                                    currentPlayers={players}
                                    maximumPlayers={Math.min(maximumPlayers, session?.remainingSlots ?? 0)}
                                    locale={selectedLanguage.code}
                                    onAccept={handleTieredGroupSizeDiscountAccept}
                                    variant="drawer"
                                    loading={bookingLoading}
                                />
                            )}
                            <div className="summary-content">
                                <SummaryCheckout
                                    enable={checkoutEnabled}
                                    loading={loading}
                                    text={checkoutText}
                                    onCheckout={checkoutClicked}
                                />

                                <div className="drawer-toggle">
                                    <ZLButton
                                        buttonId="summaryDrawToggleBtn"
                                        type={ButtonType.Link}
                                        onClick={toggleDrawer}
                                        className="drawer-toggle-button"
                                    >
                                        <Text>{t('bookingSummary.labels.summary')}</Text>
                                        <CaretDownFilled rotate={180} className={activeKey !== '' ? 'active' : ''} />
                                    </ZLButton>
                                </div>

                                <Collapse
                                    className="zl-hidden-collapse summary-collapse"
                                    bordered={false}
                                    expandIcon={(_panelProps) => <React.Fragment />}
                                    activeKey={activeKey}
                                >
                                    <Panel header="" key="summary" className="summary-panel">
                                        {isGlobalPurchase ?
                                            <SummaryItem title={globalTitle} value={globalLabel} />
                                            : <SummaryItem title={venueTitle} value={venueNode} />}

                                        <PurchaseDetails isFullWidth={false} />
                                    </Panel>
                                </Collapse>
                            </div>
                        </Col>
                    </Row>
                </div>
            </Affix>
        );
    }

    //regular old embedded version
    return (
        <ZLSection active={props.active ?? true} title={title} action={modifyButton} scrollTo={props.scrollTo}>
            <Row id="summaryContainer" justify="start" align="top" className="summary-container">
                <Col xs={24} sm={24} md={24} lg={24}>
                    <div className="summary-content">
                        <PurchaseDetails isFullWidth={true} />
                        <SummaryCheckout
                            enable={checkoutEnabled}
                            loading={loading}
                            text={checkoutText}
                            onCheckout={checkoutClicked}
                        />
                    </div>
                </Col>
            </Row>
        </ZLSection>
    );
};

export default BookingSummary;
